(() => {
  const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const hoverNavigation = window.matchMedia('(min-width: 981px) and (hover: hover) and (pointer: fine)');
  const dropdownState = new WeakMap();
  let keyboardNavigation = false;
  const dropdowns = () => Array.from(document.querySelectorAll('#PrimaryNavigation .dt-nav-dropdown'));
  const clearDropdownTimers = dropdown => {
    const state = dropdownState.get(dropdown);
    if (!state) return;
    clearTimeout(state.openTimer);
    clearTimeout(state.closeTimer);
  };
  const closeDropdown = dropdown => {
    clearDropdownTimers(dropdown);
    dropdown.open = false;
  };
  const initializeHoverNavigation = () => dropdowns().forEach(dropdown => {
    if (dropdownState.has(dropdown)) return;
    const state = {};
    dropdownState.set(dropdown, state);
    dropdown.addEventListener('pointerenter', event => {
      if (!hoverNavigation.matches || event.pointerType !== 'mouse') return;
      clearDropdownTimers(dropdown);
      if (keyboardNavigation && dropdowns().some(other => other !== dropdown && other.open && other.contains(document.activeElement))) return;
      state.openTimer = setTimeout(() => {
        if (!hoverNavigation.matches || !dropdown.isConnected) return;
        dropdowns().filter(other => other !== dropdown).forEach(closeDropdown);
        dropdown.open = true;
      }, 120);
    });
    dropdown.addEventListener('pointerleave', event => {
      if (!hoverNavigation.matches || event.pointerType !== 'mouse') return;
      clearDropdownTimers(dropdown);
      state.closeTimer = setTimeout(() => {
        if (keyboardNavigation && dropdown.contains(document.activeElement)) return;
        dropdown.open = false;
      }, 200);
    });
    dropdown.querySelector('summary')?.addEventListener('click', () => clearDropdownTimers(dropdown));
    dropdown.addEventListener('focusout', () => {
      clearTimeout(state.closeTimer);
      state.closeTimer = setTimeout(() => {
        if (hoverNavigation.matches && !dropdown.contains(document.activeElement) && !dropdown.matches(':hover')) closeDropdown(dropdown);
      }, 200);
    });
    dropdown.addEventListener('toggle', () => {
      if (hoverNavigation.matches && dropdown.open) dropdowns().filter(other => other !== dropdown).forEach(closeDropdown);
    });
  });
  document.addEventListener('pointerdown', event => {
    keyboardNavigation = false;
    if (!hoverNavigation.matches) return;
    dropdowns().filter(dropdown => !dropdown.contains(event.target)).forEach(closeDropdown);
  });
  document.addEventListener('keydown', event => {
    if (event.key === 'Tab' || event.key.startsWith('Arrow')) keyboardNavigation = true;
    if (event.key !== 'Escape' || !hoverNavigation.matches) return;
    dropdowns().filter(dropdown => dropdown.open).forEach(dropdown => {
      const focusedInside = dropdown.contains(document.activeElement);
      closeDropdown(dropdown);
      if (focusedInside) dropdown.querySelector('summary')?.focus({ preventScroll: true });
    });
  });
  hoverNavigation.addEventListener('change', () => dropdowns().forEach(closeDropdown));
  document.addEventListener('shopify:section:load', initializeHoverNavigation);
  initializeHoverNavigation();
  const animateOnce = element => {
    if (motion.matches || typeof element.animate !== 'function') return;
    element.animate([{ opacity: .65, transform: 'translateY(10px)' }, { opacity: 1, transform: 'none' }], { duration: 400, easing: 'cubic-bezier(.22,.7,.3,1)' });
  };
  if ('IntersectionObserver' in window && !motion.matches) {
    const observer = new IntersectionObserver(entries => entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      if (entry.boundingClientRect.top > 0) animateOnce(entry.target);
      observer.unobserve(entry.target);
    }), { threshold: .12 });
    document.querySelectorAll('#MainContent .section-heading, #MainContent .custom-service__content, #MainContent .finder-card').forEach(element => {
      if (element.getBoundingClientRect().top >= window.innerHeight) observer.observe(element);
    });
    motion.addEventListener('change', () => { if (motion.matches) observer.disconnect(); });
  }
  document.querySelectorAll('.product-gallery__main').forEach(gallery => {
    const observer = new MutationObserver(() => {
      if (!motion.matches) gallery.querySelector('img')?.animate([{ opacity: .65 }, { opacity: 1 }], { duration: 180, easing: 'ease-out' });
    });
    observer.observe(gallery, { attributes: true, attributeFilter: ['src'], subtree: true });
  });
  const initializers = new WeakMap();
  const initializeOffer = root => {
    if (!root || initializers.has(root)) return;
    const dialog = root.querySelector('dialog');
    const launcher = root.querySelector('[data-offer-open]');
    if (!dialog || typeof dialog.showModal !== 'function') return;
    const code = root.dataset.code;
    const key = `dailytime.welcome.${code}.v1`;
    const cooldown = Number(root.dataset.cooldown || 7) * 86400000;
    let state = {};
    let timer;
    let closeTimer;
    let autoOpened = false;
    let busyRetries = 0;
    let returnFocus;
    const controller = new AbortController();
    const events = { signal: controller.signal };
    try { state = JSON.parse(localStorage.getItem(key) || '{}') || {}; } catch { state = {}; }
    const remember = days => {
      state = { until: Date.now() + days * 86400000 };
      try { localStorage.setItem(key, JSON.stringify(state)); } catch {}
    };
    const isBusy = () => document.hidden || document.documentElement.matches('.cart-drawer-open, .mobile-nav-open, .lightbox-open, .catalog-filter-open') || !!document.querySelector('dialog[open], [role="dialog"][aria-hidden="false"], .shopify-pc__banner__dialog') || !!document.activeElement?.matches('input, textarea, select, [contenteditable="true"]');
    const cleanupClose = () => {
      clearTimeout(closeTimer);
      dialog.classList.remove('dt-offer-closing');
      document.documentElement.classList.remove('welcome-offer-open');
      if (returnFocus?.isConnected && returnFocus !== document.body) returnFocus.focus({ preventScroll: true });
    };
    const close = () => {
      if (!dialog.open || dialog.classList.contains('dt-offer-closing')) return;
      clearTimeout(timer);
      remember(Math.max(Number(root.dataset.cooldown || 7), (state.until - Date.now()) / 86400000 || 0));
      if (motion.matches) { dialog.close(); cleanupClose(); return; }
      dialog.classList.add('dt-offer-closing');
      closeTimer = setTimeout(() => { dialog.close(); cleanupClose(); }, 180);
    };
    const open = automatic => {
      if (dialog.open || isBusy()) return false;
      if (automatic && (autoOpened || Number(state.until || 0) > Date.now())) return false;
      clearTimeout(timer);
      returnFocus = document.activeElement;
      root.querySelector('[data-offer-status]').textContent = '';
      dialog.showModal();
      document.documentElement.classList.add('welcome-offer-open');
      autoOpened = true;
      remember(cooldown / 86400000);
      return true;
    };
    launcher.hidden = false;
    launcher.addEventListener('click', () => open(false), events);
    root.querySelectorAll('[data-offer-close]').forEach(button => button.addEventListener('click', close, events));
    dialog.addEventListener('cancel', event => { event.preventDefault(); close(); }, events);
    dialog.addEventListener('keydown', event => {
      if (event.key !== 'Tab') return;
      const controls = Array.from(dialog.querySelectorAll('button:not(:disabled), a[href]')).filter(element => element.getClientRects().length);
      const first = controls[0];
      const last = controls[controls.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    }, events);
    dialog.addEventListener('close', cleanupClose, events);
    let backdropPointer = false;
    dialog.addEventListener('pointerdown', event => {
      const r = dialog.getBoundingClientRect();
      backdropPointer = event.target === dialog && (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom);
    }, events);
    dialog.addEventListener('click', event => {
      const r = dialog.getBoundingClientRect();
      if (backdropPointer && event.target === dialog && (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom)) close();
      backdropPointer = false;
    }, events);
    root.querySelector('[data-offer-copy]').addEventListener('click', async () => {
      const status = root.querySelector('[data-offer-status]');
      try {
        await navigator.clipboard.writeText(code);
        status.textContent = root.dataset.copied;
        remember(30);
      } catch {
        const range = document.createRange();
        range.selectNodeContents(root.querySelector('[data-offer-code]'));
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
        status.textContent = root.dataset.copyFailed;
      }
    }, events);
    root.querySelector('[data-offer-apply]').addEventListener('click', () => { remember(30); clearTimeout(timer); }, events);
    const attemptAuto = () => {
      if (Number(state.until || 0) > Date.now() || autoOpened) return;
      if (document.activeElement?.closest('.dt-offer-launcher')) return;
      if (isBusy()) {
        if (++busyRetries < 45) timer = setTimeout(attemptAuto, 2000);
        return;
      }
      open(true);
    };
    if (!window.Shopify?.designMode && !document.body.classList.contains('dt-bf-season')) timer = setTimeout(attemptAuto, Number(root.dataset.delay || 12000));
    initializers.set(root, () => { clearTimeout(timer); clearTimeout(closeTimer); controller.abort(); if (dialog.open) dialog.close(); cleanupClose(); });
  };
  document.querySelectorAll('[data-welcome-offer]').forEach(initializeOffer);
  document.addEventListener('shopify:section:load', event => initializeOffer(event.target.querySelector('[data-welcome-offer]')));
  document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll('[data-welcome-offer]').forEach(root => initializers.get(root)?.()));
})();
